import { Box, Button, TextField, SelectChangeEvent, Grid, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { FilterList, FilterListOff } from "@mui/icons-material";
import { useEffect, useState } from "react";

import { AdminLogsFiltersStyle } from "./style";

interface IDefaultValues {
    search: string;
    userId: number | null;
    action: string | null;
    entity: string | null;
    startDate: string;
    endDate: string;
}

export const defaultValues: IDefaultValues = {
    search: "",
    userId: null,
    action: null,
    entity: null,
    startDate: "",
    endDate: "",
};

const ACTIONS = [
    "VIEW",
    "CREATE",
    "UPDATE",
    "DELETE",
    "LOGIN",
    "LOGOUT",
    "EXPORT",
    "IMPORT",
    "ACTIVATE",
    "DEACTIVATE",
    "APPROVE",
    "REJECT",
    "OTHER",
];

const ENTITIES = [
    "users",
    "customers",
    "countries",
    "contents",
    "genres",
    "publishers",
    "tags",
    "videos",
    "coupons",
    "payments",
    "subscriptions",
    "plans",
    "static-pages",
];

const localFilters = localStorage.getItem("AdminLogsFilters");
const prevFilter = localFilters ? JSON.parse(localFilters) : "";

interface AdminLogsFiltersProps {
    onApply: (data: IDefaultValues) => void;
    users?: Array<{ id: number, firstName: string, lastName: string, email: string }>;
}

export default function AdminLogsFilters({ onApply, users = [] }: AdminLogsFiltersProps) {
    const [fields, setFields] = useState<IDefaultValues>(prevFilter || defaultValues);
    const [filterActivated, setFilterActivated] = useState<boolean>(false);

    const handleApply = () => onApply && onApply(fields);
    const toggleFilters = () => setFilterActivated((prev) => !prev);
    const handleReset = () => {
        setFields(defaultValues);
        localStorage.setItem("AdminLogsFilters", JSON.stringify(defaultValues));
        if (onApply) onApply(defaultValues);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
        const name = event.target.name;
        const value = event.target.value;

        setFields((prev) => {
            const filterData = {
                ...prev,
                [name]: value,
            };

            localStorage.setItem("AdminLogsFilters", JSON.stringify(filterData));

            return filterData;
        });
    };

    return (
        <AdminLogsFiltersStyle>
            <Box className="filter-wrapper">
                {filterActivated && (
                    <Grid container alignItems="center" spacing={1}>
                        <Grid item lg={3} md={4} xs={12}>
                            <TextField
                                fullWidth
                                className="input field "
                                label="Search"
                                name="search"
                                placeholder="Search in logs..."
                                size="small"
                                value={fields.search}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item lg={3} md={4} xs={12}>
                            <FormControl fullWidth className="select field" size="small">
                                <InputLabel id="user-select-label">User</InputLabel>
                                <Select
                                    id="user-select"
                                    label="User"
                                    labelId="user-select-label"
                                    name="userId"
                                    value={String(fields.userId)}
                                    onChange={handleChange}
                                >
                                    <MenuItem value={null as any}>All Users</MenuItem>
                                    {users.map((user) => (
                                        <MenuItem key={user.id} value={user.id}>
                                            {user.firstName && user.lastName
                                                ? `${user.firstName} ${user.lastName}`
                                                : user.email}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item lg={3} md={4} xs={12}>
                            <FormControl fullWidth className="select field" size="small">
                                <InputLabel id="action-select-label">Action</InputLabel>
                                <Select
                                    id="action-select"
                                    label="Action"
                                    labelId="action-select-label"
                                    name="action"
                                    value={String(fields.action)}
                                    onChange={handleChange}
                                >
                                    <MenuItem value={null as any}>All Actions</MenuItem>
                                    {ACTIONS.map((action) => (
                                        <MenuItem key={action} value={action}>
                                            {action}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item lg={3} md={4} xs={12}>
                            <FormControl fullWidth className="select field" size="small">
                                <InputLabel id="entity-select-label">Entity</InputLabel>
                                <Select
                                    id="entity-select"
                                    label="Entity"
                                    labelId="entity-select-label"
                                    name="entity"
                                    value={String(fields.entity)}
                                    onChange={handleChange}
                                >
                                    <MenuItem value={null as any}>All Entities</MenuItem>
                                    {ENTITIES.map((entity) => (
                                        <MenuItem key={entity} value={entity}>
                                            {entity.charAt(0).toUpperCase() + entity.slice(1).replace("-", " ")}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item lg={3} md={4} xs={12}>
                            <TextField
                                fullWidth
                                className="input field"
                                label="Start Date"
                                name="startDate"
                                type="date"
                                size="small"
                                value={fields.startDate}
                                onChange={handleChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item lg={3} md={4} xs={12}>
                            <TextField
                                fullWidth
                                className="input field"
                                label="End Date"
                                name="endDate"
                                type="date"
                                size="small"
                                value={fields.endDate}
                                onChange={handleChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item lg={3} xs={12}>
                            <Box className="actions">
                                <Button className="action-btn" variant="contained" onClick={handleApply}>
                                    Apply
                                </Button>
                                <Button className="action-btn" variant="contained" onClick={handleReset}>
                                    Reset
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Box>
            <Button
                className="filter-btn"
                startIcon={filterActivated ? <FilterListOff /> : <FilterList />}
                variant="contained"
                onClick={toggleFilters}
            >
                Filters
            </Button>
        </AdminLogsFiltersStyle>
    );
}
